import { useEffect, useState } from "react";
import { Button, Dialog, EmptyState, FormRow, FormSection, Icon, Meter, Notice, Pill, Skeleton, Tabs, cx, formatDate, formatMoney, formatNumber } from "@omnabase/ui";
import { useSearchParams } from "react-router";
import { PAID_PLAN_IDS, aboutPages, isLiveSubscription, knowledgeAllowance, type CreditsRead, type PacksRead, type Plan, type PlansRead, type WorkspacePlan } from "@omnabase/shared";
import { api } from "../../lib/api";
import { storageState, useCheckoutReturn, useStripePages } from "../../lib/billing";
import { useCan } from "../../lib/access";
import { useScrollReset } from "../../lib/scrollReset";
import { useNav, useSetParam, useWs } from "../../lib/nav";
import { useSession } from "../../shell/store";
import { InvoicesTab, PaymentDetailsTab } from "./BillingStripeTabs";
import { CreditHistory } from "./CreditHistory";
import { CreditsSection } from "./BillingCredits";

// Settings › Billing (M7 round six, the part before Stripe; brief task 5), ported from the prototype's BillingSettings.tsx: the plan
// and the way up, the credits with their meter, the caps, the usage by project and the credit history. Round four brought Stripe: the
// Change plan dialog opens Stripe's Checkout for a workspace without a live subscription and the Portal once there is one, the plan
// card shows the subscription's state (renews, ends, payment failed), and Payment details and Invoices read Stripe (`BillingStripeTabs`).
// Round five: "Buy credits" opens the packs dialog (`?buy=1`; a pack's button opens Stripe's one-off Checkout) and the Credits section
// carries auto-reload's switch and choices. Owners (the `billing` capability) see it; the rest see the owner-only empty state.
const TABS = [{ id: "overview", label: "Overview" }, { id: "payment", label: "Payment details" }, { id: "invoices", label: "Invoices" }];
/** what a credit buys and how credits last, one rule per line */
const PAID = PAID_PLAN_IDS as readonly string[];
const money = (cents: number) => formatMoney(cents / 100, "EUR", cents % 100 !== 0);
const plural = (n: number, one: string, many: string) => `${formatNumber(n)} ${n === 1 ? one : many}`;
type Pack = PacksRead["rows"][number];
const packName = (p: Pack) => `${formatNumber(p.credits)} credits`;

/** the packs on sale (`GET /packs`), read once for the owner: the packs dialog and auto-reload's menu share them */
function usePacks(can: boolean, slug: string): Pack[] | null {
  const [packs, setPacks] = useState<Pack[] | null>(null);
  useEffect(() => {
    if (!can) return;
    let alive = true;
    api.cached<PacksRead>(`/api/v1/w/${slug}/packs`).then((r) => { if (alive) setPacks(r.rows); }).catch(() => { if (alive) setPacks([]); });
    return () => { alive = false; };
  }, [can, slug]);
  return packs;
}

export function BillingSettings() {
  const nav = useNav(); const { ws } = useWs();
  const { workspace } = useSession();
  const can = useCan()("billing");
  const [params] = useSearchParams();
  const setParam = useSetParam();
  const [credits, setCredits] = useState<CreditsRead | null>(null);
  const packs = usePacks(can, workspace.slug);
  useCheckoutReturn();
  const tab = TABS.find((t) => t.id === params.get("tab"))?.id ?? "overview";
  const top = useScrollReset(tab); // a tab starts at the top (polish round one)
  useEffect(() => {
    if (!can) return;
    let alive = true;
    api.cached<CreditsRead>(`/api/v1/w/${workspace.slug}/credits`).then((c) => { if (alive) setCredits(c); }).catch(() => {});
    return () => { alive = false; };
  }, [can, workspace.slug, workspace.credits.balance]);
  if (!can) return <EmptyState icon="card" title="Billing belongs to the account owner" text="Plan, credits, payment and invoices are only shown to the owner of this workspace." />;

  const plan = workspace.plan;
  const link = (to: string, text: string) => <a className="ob-link" href={ws(to)} onClick={(e) => nav(ws(to), e)}>{text}</a>;
  const usage = (credits?.usage.byProject ?? []).map((u) => ({ ...u, name: workspace.projects.find((p) => p.id === u.projectId)?.name ?? "A removed project" }));

  return (
    <section className="ob-profile" aria-label="Billing">
      <header className="ob-profile__head">
        <div className="ob-settings__head"><div><h1 className="ob-page__title">Billing</h1><p className="ob-page__subtitle">What this workspace pays for: the plan a month, and credits for the AI's own work. Only the account owner sees this page.</p></div></div>
        <Tabs variant="line" items={TABS} active={tab} onChange={(id) => setParam("tab", id === "overview" ? null : id)} />
      </header>
      {/* 2026-10-19: the scroll area is the page's main landmark (the shell has none); the section keeps Team's shared layout */}
      <main className="ob-settings__scroll" ref={top}><div className="ob-form">
        {tab === "overview" && <>
        <PlanSection plan={plan} onChange={() => setParam("plans", "1")} />

        <CreditsSection plan={plan} credits={credits} packs={packs} onBuy={() => setParam("buy", "1")} onInvoices={() => setParam("tab", "invoices")} />

        <FormSection title="What the plan allows" intro={<>People are managed under {link("/settings/team", "Team")}, projects under {link("/settings/projects", "Projects")}. A bigger plan makes room.</>}>
          <CapRow used={plan.used.members} cap={plan.limits.members} noun="members" plan={plan.name} full="the next invite needs a bigger plan" more="can be invited" note="Active and invited people count; deactivated ones do not." />
          <CapRow used={plan.used.projects} cap={plan.limits.projects} noun="projects" plan={plan.name} full="the next project needs a bigger plan" more="more" note="Archived projects do not count." />
          <FormRow label={`${formatNumber(plan.limits.kbWordsPerProject)} words of knowledge per project · about ${formatNumber(aboutPages(plan.limits.kbWordsPerProject))} pages`} help="Switched-on articles, crawled pages, files and repository folders count. Each project's meter is on its Knowledge › Sources page.">{null}</FormRow>
        </FormSection>

        <FormSection title="Usage this period" intro={<>Credits by project since the period began. How well the AI does is under {link("/reports/ai", "Reports › AI performance")}.</>}>
          {!credits ? <SkeletonRows /> : usage.length === 0 ? <p className="ob-form-row__help">Nothing used yet this period.</p> : (
            <table className="ob-invoice" aria-label="Credits by project this period">
              <tbody>
                {usage.map((u) => <tr key={u.projectId}><th scope="row">{u.name}<span className="ob-invoice__detail">{plural(u.replies, "reply", "replies")}{u.investigations > 0 && ` · ${plural(u.investigations, "investigation", "investigations")}`}</span></th><td>{formatNumber(u.credits)} credits</td></tr>)}
                <tr className="ob-invoice__total"><th scope="row">Used this period</th><td>{formatNumber(credits.usage.period.credits)} credits</td></tr>
              </tbody>
            </table>
          )}
        </FormSection>

        <CreditHistory ledger={credits?.ledger ?? null} />
        </>}

        {tab === "payment" && <PaymentDetailsTab />}
        {tab === "invoices" && <InvoicesTab />}
      </div></main>
      {params.get("plans") && <PlansDialog plan={plan} onClose={() => setParam("plans", null)} />}
      {params.get("buy") && <PacksDialog packs={packs} onClose={() => setParam("buy", null)} />}
    </section>
  );
}

/** The plan card: name, price, and the period's word from the subscription ("renews", or "ends" once a cancellation is scheduled);
 *  past due, the notice that the payment failed with the way to Stripe's Portal. */
function PlanSection({ plan, onChange }: { plan: WorkspacePlan; onChange: () => void }) {
  const { busy, portal } = useStripePages();
  const sub = plan.subscription;
  const period = plan.periodEnd ? ` · ${sub.cancelAtPeriodEnd && isLiveSubscription(sub.status) ? "ends" : "renews"} ${formatDate(plan.periodEnd)}` : null;
  return (
    <FormSection title="Plan">
      {sub.status === "past_due" && <Notice icon="alarm" action={<Button variant="primary" size="sm" disabled={busy === "portal"} onClick={portal}>Manage on Stripe</Button>}><strong>The last payment failed.</strong> Stripe tries the card again over the next days and the plan keeps working meanwhile. Update the card on Stripe's page to settle it.</Notice>}
      <div className="ob-plan">
        <div className="ob-plan__text"><strong className="ob-plan__name">{plan.name}</strong>
          <span className="ob-plan__price">{plan.priceMonthCents === null ? <><strong>No charge</strong>{period ?? " · no monthly invoice"}</> : <><strong>{money(plan.priceMonthCents)}</strong> a month{period ?? ""}</>}</span></div>
        <Button onClick={onChange}>Change plan</Button>
      </div>
      {plan.storage && <StorageLine used={plan.storage.used} cap={plan.storage.cap} />}
    </FormSection>
  );
}

/** the files round (2026-10-30; brief rule 5): how full the workspace's storage is, always; past 80 percent in the warning tone with the way to
 *  more room, at full in the error tone with what that means */
function StorageLine({ used, cap }: { used: number; cap: number }) {
  const s = storageState(used, cap);
  const more = s.tone === "error" ? " · New files are refused; customers' mails still arrive, without their files." : s.tone === "warning" ? " · A larger plan gives you more room: Change plan." : "";
  return <p className={cx("ob-plan__storage", s.tone !== "normal" && `ob-plan__storage--${s.tone}`)}>{s.line}{more}</p>;
}

/** The packs on sale, one card each (credits, price, the price a credit), each buying through Stripe's one-off Checkout. */
function PacksDialog({ packs, onClose }: { packs: Pack[] | null; onClose: () => void }) {
  const stripe = useStripePages();
  const intro = "A pack's credits never expire and are used after the included ones. Buying opens Stripe's checkout, which keeps the card for auto-reload.";
  return (
    <Dialog title="Buy credits" intro={intro} width={760} onClose={onClose} footer={<><span className="ob-help__foot">Prices without VAT.</span><Button onClick={onClose}>Close</Button></>}>
      {packs === null ? <PlanCardsSkeleton count={3} /> : packs.length === 0 ? <p className="ob-form-row__help">No packs are on sale right now.</p> : (
        <div className="ob-plans">
          {packs.map((p) => <section key={p.id} className="ob-plans__card" aria-label={packName(p)}>
            <h3 className="ob-plans__name">{packName(p)}</h3>
            <p className="ob-plans__price"><strong>{money(p.priceCents)}</strong><span>{money(Math.round(p.priceCents / p.credits))} a credit</span></p>
            <Button variant="secondary" disabled={stripe.busy === p.id} onClick={() => stripe.pack(p.id)}>Buy for {money(p.priceCents)}</Button>
          </section>)}
        </div>
      )}
    </Dialog>
  );
}

/** one cap of the plan: "<used> of <cap> <noun>" with a meter, or the count with "No cap on <plan>" */
function CapRow({ used, cap, noun, plan, full, more, note }: { used: number; cap: number | null; noun: string; plan: string; full: string; more: string; note: string }) {
  const label = cap === null ? `${formatNumber(used)} ${noun}` : `${formatNumber(used)} of ${formatNumber(cap)} ${noun}`;
  const help = cap === null ? `No cap on ${plan}.` : used >= cap ? `${plan} is full: ${full}.` : `${formatNumber(cap - used)} ${more} on ${plan}. ${note}`;
  return <FormRow label={label} help={help}>{cap !== null && <span style={{ width: 220 }}><Meter value={used} max={cap} label={`${noun[0]!.toUpperCase()}${noun.slice(1)} in use`} /></span>}</FormRow>;
}

/** The live plans side by side: price a month, credits, members, projects, the knowledge size in words and pages (2026-10-13), what each switches on. Without a live subscription each
 *  paid plan the workspace fits offers Stripe's Checkout ("Upgrade to …"); Free and Beta are never bought. With one, every change goes
 *  through Stripe's Portal, so the dialog offers that instead and no second checkout. */
function PlansDialog({ plan, onClose }: { plan: WorkspacePlan; onClose: () => void }) {
  const { workspace } = useSession();
  const stripe = useStripePages();
  const live = isLiveSubscription(plan.subscription.status);
  const [rows, setRows] = useState<Plan[] | null>(null);
  useEffect(() => { api.cached<PlansRead>(`/api/v1/w/${workspace.slug}/plans`).then((r) => setRows(r.rows.filter((p) => p.id !== "beta" || p.id === plan.id))).catch(() => setRows([])); }, [workspace.slug, plan.id]);
  const intro = live
    ? "Going up, going down and cancelling happen on Stripe's page, which keeps the card and the invoices. Going down waits for the end of the paid period."
    : "Choosing a plan opens Stripe's checkout; the plan applies as soon as the payment goes through. Monthly only during the beta.";
  const footer = <><span className="ob-help__foot">Prices without VAT.</span>{live && <Button variant="primary" disabled={stripe.busy === "portal"} onClick={stripe.portal}>Manage on Stripe</Button>}<Button onClick={onClose}>Close</Button></>;
  return (
    <Dialog title="Change plan" intro={intro} width={1120} onClose={onClose} footer={footer}>
      {rows === null ? <PlanCardsSkeleton count={4} /> : (
        <div className={cx("ob-plans", rows.length >= 4 && "ob-plans--four")}>
          {rows.map((p) => <PlanCard key={p.id} p={p} plan={plan} action={live ? null : purchase(p, plan, stripe)} />)}
        </div>
      )}
    </Dialog>
  );
}

type Purchase = { label: string; busy: boolean; onClick: () => void };
/** what buying `p` would be for a workspace without a live subscription: none for its own plan, Free or Beta; "Upgrade to" a dearer
 *  plan, "Switch to" a cheaper one */
function purchase(p: Plan, plan: WorkspacePlan, stripe: ReturnType<typeof useStripePages>): Purchase | null {
  if (p.id === plan.id || !PAID.includes(p.id)) return null;
  const up = (p.priceMonthCents ?? 0) > (plan.priceMonthCents ?? 0);
  return { label: `${up ? "Upgrade to" : "Switch to"} ${p.name}`, busy: stripe.busy === p.id, onClick: () => stripe.checkout(p.id as (typeof PAID_PLAN_IDS)[number]) };
}
/** why the workspace does not fit `p`: its people or projects are more than `p` allows (never for its own plan) */
function blockedBy(p: Plan, plan: WorkspacePlan): string | null {
  if (p.id === plan.id) return null;
  if (p.limits.members !== null && plan.used.members > p.limits.members) return `${formatNumber(plan.used.members)} people are more than ${p.name} allows.`;
  if (p.limits.projects !== null && plan.used.projects > p.limits.projects) return `${formatNumber(plan.used.projects)} projects are more than ${p.name} allows.`;
  return null;
}

/** a plan card's credits in a few words; a plan without AI says so instead of "0 credits once" (round three, 2026-10-19) */
const planCredits = (p: Plan): string => (!p.features.ai ? "No AI" : p.credits.includedMonth ? `${formatNumber(p.credits.includedMonth)} credits a month` : p.credits.starterOnce ? `${formatNumber(p.credits.starterOnce)} credits once` : "Credits by pack");
function PlanCard({ p, plan, action }: { p: Plan; plan: WorkspacePlan; action: Purchase | null }) {
  const cap = (n: number | null, noun: string) => (n === null ? `Unlimited ${noun}` : `${formatNumber(n)} ${n === 1 ? noun.replace(/s$/, "") : noun}`);
  const features = [p.features.investigation && "Investigations", p.features.mcp && "Your own agent through MCP", p.features.ownDomains && "Own domains"].filter((f): f is string => !!f); // 2026-10-18: automatic replies are hidden entirely; the plan's autoSend stays in the data, unshown
  const mine = p.id === plan.id; const blocked = blockedBy(p, plan);
  return (
    <section className={cx("ob-plans__card", mine && "ob-plans__card--current")} aria-label={p.name}>
      <h3 className="ob-plans__name">{p.name}{mine && <Pill>Your plan</Pill>}</h3>
      <p className="ob-plans__price"><strong>{p.priceMonthCents === null ? "No charge" : money(p.priceMonthCents)}</strong>{p.priceMonthCents === null ? "" : " a month"}<span>{planCredits(p)}</span></p>
      <ul className="ob-plans__list">{[cap(p.limits.members, "members"), cap(p.limits.projects, "projects"), knowledgeAllowance(p.limits.kbWordsPerProject), ...features].map((f) => <li key={f}><Icon name="check" size="sm" />{f}</li>)}</ul>
      {blocked && <p className="ob-plans__blocked">{blocked}</p>}
      {action && <Button variant="secondary" disabled={!!blocked || action.busy} onClick={action.onClick}>{action.label}</Button>}
    </section>
  );
}

/** polish round two: grey, still blocks while Billing's reads are on their way (no "Loading…") */
const SkeletonRows = () => <div className="ob-skeleton-lines" aria-busy="true"><Skeleton shape="line" /><Skeleton shape="line" /><Skeleton shape="line" width="60%" /></div>;
const PlanCardsSkeleton = ({ count }: { count: number }) => <div className="ob-plans" aria-busy="true">{Array.from({ length: count }, (_, i) => <Skeleton key={i} shape="card" height={220} />)}</div>;
